/**
 * AssessmentInstructionsDialog
 *
 * Top-bar "How this works" button + Dialog wrapper around the shared
 * AssessmentInstructionsContent. Mounted globally in CandidateShell so
 * a candidate can pop open the same orientation copy from any candidate
 * page (Welcome, TP, AQ, assessments list) as a mid-flow refresher.
 *
 * The same content also renders inline on CandidateWelcomePage so a
 * first-time test taker sees the orientation without needing to click
 * the button — see AssessmentInstructionsContent for the actual copy.
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { HelpCircle } from "lucide-react";
import { apiClient } from "../../services/apiClient";
import { withActiveTestId } from "../../stores/activeTestStore";
import { isClientTPScenario } from "../../constants/scenarios";
import { AssessmentInstructionsContent } from "../AssessmentInstructionsContent";

interface ScenarioInfo {
  id?: string;
  name?: string;
  has_comparison?: boolean;
  skip_tp?: boolean;
}

interface AssessmentInfo {
  scenario?: ScenarioInfo;
}

export function AssessmentInstructionsDialog() {
  const [open, setOpen] = useState(false);
  const [skipTP, setSkipTP] = useState(false);
  const [hasComparison, setHasComparison] = useState(false);

  // Lazy fetch — only hit the API when the dialog first opens, so we
  // don't add a redundant request to every candidate page mount.
  const [fetched, setFetched] = useState(false);
  useEffect(() => {
    if (!open || fetched) return;
    let cancelled = false;
    apiClient
      .get<AssessmentInfo>(withActiveTestId("/api/candidate/assessment"))
      .then((data) => {
        if (cancelled) return;
        setSkipTP(isClientTPScenario(data.scenario));
        setHasComparison(data.scenario?.has_comparison === true);
        setFetched(true);
      })
      .catch(() => {
        // Safe default: assume candidate-owned TP (Sc.1–3). If the fetch
        // fails the dialog is still useful with a sensible flow shown.
        if (!cancelled) setFetched(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, fetched]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-gray-600 hover:text-brand-navy inline-flex items-center gap-1.5 transition-colors"
          aria-label="How this assessment works"
        >
          <HelpCircle size={14} aria-hidden="true" />
          <span className="text-xs">How this works</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white rounded-xl shadow-2xl max-w-xl border-brand-steel/50 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-gray-900">
            How this assessment works
          </DialogTitle>
          <DialogDescription className="text-sm text-brand-steel">
            A quick reference you can open any time during the assessment.
          </DialogDescription>
        </DialogHeader>

        <div className="pt-2">
          <AssessmentInstructionsContent
            skipTP={skipTP}
            hasComparison={hasComparison}
            density="compact"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AssessmentInstructionsDialog;
